// diag.ts — an on-screen readout for questions only a phone can answer.
//
// Born 2026-08-04. The chip stayed on Parikara's casino home and the two candidate
// causes — "the gate never ran because the poll died" and "the gate ran and the DOM
// lied to it" — look identical from the outside. The desktop cannot separate them:
// Parikara unmounts <sport-mobile> cleanly there. And the obvious phone-side tell
// ("is the chip's data still moving?") is worthless, because a football score can
// legitimately sit at 0 – 0 for an hour.
//
// So the overlay states its own readings on screen. No console, no bookmarklet #2:
// load the sportsbook once with `?bcdiag=1`, and the strip survives from there
// (the flag is remembered — their router drops the query string on a tab switch,
// which is exactly the moment being investigated).

import { readHostGeometry } from "./host-geometry";
import type { StakeLiveness } from "./adapter";

const FLAG_KEY = "bc_diag";

/** On when the URL says so, or when a previous load turned it on. `?bcdiag=0` clears it. */
export function diagEnabled(): boolean {
  try {
    if (/[?&]bcdiag=0/.test(location.search)) {
      localStorage.removeItem(FLAG_KEY);
      return false;
    }
    if (/[?&]bcdiag=1/.test(location.search)) {
      try { localStorage.setItem(FLAG_KEY, "1"); } catch { /* private mode */ }
      return true;
    }
    return localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

export interface DiagRead {
  /** ms since the last poll that actually produced suggestions; null = never. */
  pollAgeMs: number | null;
  pool: number;
  suggestions: number;
  /** What the overlay is currently doing with the chip — "", "none", or "no chip". */
  chipDisplay: string;
  /** The last add attempt, already formatted. Empty until one happens — the line
   *  is then STICKY, because the answer has to survive the card flipping to
   *  "не удалось добавить" and the owner reaching for a screenshot. */
  lastAdd?: string;
  /** The starred-favourites cache, already formatted. Always present on Digitain —
   *  "none" is the reading that matters most, because it is what a session looks
   *  like before the user has ever opened their ★ Фавориты tab. */
  favourites?: string;
  /** Why the ★ is or is not on the card — `starGateLine()`. The favourites CACHE
   *  above answers a DIFFERENT question (what we hold), and reading it as this one
   *  is how the missing star went uninvestigated for a day. */
  starGate?: string;
  /** The bet-history profile and how many reads it has spent — the request budget
   *  is the thing most worth watching on a real board. */
  betHistory?: string;
  /** How many skips this session is carrying, and which session key holds them.
   *  ⛔ The count being 0 right after a skip is the signature of the 2026-09-01
   *  bug: the SPA remounted us and the list was memory-only. */
  skips?: string;
  /** The pick the pill is holding, if any — label, live price, and how long it
   *  has been held. Empty when the pill is on the normal rotation. */
  hold?: string;
}

const yn = (v: boolean | null): string => (v == null ? "n/a" : v ? "yes" : "NO");

/** One leg of an add, as the board saw it at the moment we posted. */
export interface AddProbeLeg {
  stakeId: number;
  state: StakeLiveness;
  /** Odds the CARD was offering. */
  expected: number | null;
  /** Odds the board holds now; null when the stake is gone. */
  odds: number | null;
  /** Age of the board record this reading came from. */
  ageMs: number | null;
}

const o = (n: number | null): string => (n == null ? "—" : n.toFixed(2));

/**
 * The one line that separates "the pick went stale" from "the operator said no".
 *
 * Written for an 11px monospace strip on a phone, so a single-leg add stays on one
 * line and only a combo spills to one line per leg.
 */
export function formatAddProbe(
  legs: AddProbeLeg[],
  verdict: string,
  posted: number,
  landed: number,
): string {
  const leg = (l: AddProbeLeg): string => {
    const age = l.ageMs == null ? "age=?" : `age=${Math.round(l.ageMs / 1000)}s`;
    const price = l.state === "moved" ? `${o(l.expected)}→${o(l.odds)}` : o(l.expected);
    return `${l.stakeId} ${l.state.toUpperCase()} ${price} ${age}`;
  };
  const head = `${verdict} posted=${posted} landed=${landed}`;
  if (legs.length === 1) return `${head}  ${leg(legs[0])}`;
  // 7 spaces = the width of the "add    " label, so the legs line up under it.
  return [head, ...legs.map((l) => `       ${leg(l)}`)].join("\n");
}

/** Render the strip and keep it current. Returns a stop function. */
export function startDiag(
  sportsbookRoot: string | undefined,
  read: () => DiagRead,
  apiKey = "",
  intervalMs = 1000,
): () => void {
  const box = document.createElement("div");
  // Top of the screen on purpose: the chip lives at the bottom, and the strip must
  // not cover the thing being diagnosed.
  box.setAttribute("style", [
    "position:fixed", "top:0", "left:0", "right:0", "z-index:2147483647",
    "background:rgba(0,0,0,.86)", "color:#7CFFB2", "padding:6px 8px",
    "font:11px/1.35 ui-monospace,Menlo,monospace", "white-space:pre",
    "pointer-events:none",
  ].join(";"));
  document.body.appendChild(box);

  const tick = (): void => {
    const g = readHostGeometry(sportsbookRoot);
    const r = read();
    const age = r.pollAgeMs == null ? "never" : `${Math.round(r.pollAgeMs / 1000)}s ago`;
    box.textContent = [
      // The KEY first, because the one real bug this strip has caught so far was
      // the wrong operator: a bookmarklet dragged off the install page before the
      // operator switch was flipped runs Hannibalbet's config on Parikara, which
      // declares no sportsbook root — so the gate silently never arms and every
      // symptom points at the gate instead of at the key (owner, 2026-08-04).
      `key    ${apiKey || "(unknown)"}`,
      `path   ${location.pathname}`,
      `root   ${sportsbookRoot || "(none declared)"} present=${yn(g.sportsbookPresent)} showing=${yn(g.sportsbookMounted)}`,
      `gate   ${g.sportsbookMounted === false ? "HIDE" : "show"}   chip display="${r.chipDisplay}"`,
      `poll   ${age}   pool=${r.pool} sugg=${r.suggestions}`,
      ...(r.skips ? [`skips  ${r.skips}`] : []),
      ...(r.hold ? [`hold   ${r.hold}`] : []),
      ...(r.favourites ? [`fav    ${r.favourites}`] : []),
      ...(r.starGate ? [`star   ${r.starGate}`] : []),
      ...(r.betHistory ? [`bets   ${r.betHistory}`] : []),
      // Only after an add — before that there is nothing true to say about one.
      ...(r.lastAdd ? [`add    ${r.lastAdd}`] : []),
    ].join("\n");
  };
  tick();
  const timer = setInterval(tick, intervalMs);
  return () => { clearInterval(timer); box.remove(); };
}
